import {assertAndContinue} from 'Asserts';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  schemaFieldsetName,
  schemaFieldsetNamePluralized,
} from 'schemas/schema-interface/util/schemaFieldsetName';
import getSchemaInterfaceURL from 'schemas/schema-interface/v2/getSchemaInterfaceURL';
import {getItemTypeDisplayName} from 'schemas/TagSchemaItemTypeUtil';
import * as logging from 'util/Logging';
import {assertNever, Optional} from 'util/Types';

// Please keep in sync with benchling/schemas/validation/schema_interface_fields.py
export enum SchemaInterfaceConstraintErrorType {
  MISSING_FIELD = 'missing_field',
  INVALID_FIELD = 'invalid_field',
  INVALID_ARCHIVE = 'invalid_archive',
  INVALID_FORMULA_FIELDS = 'invalid_formula_fields',
  OVERLAPPING_FIELDS = 'overlapping_field',
  INVALID_BASE_BIOENTITY_TYPE = 'invalid_base_bioentity_type',
  STATIC_SCHEMA_INTERFACE_VALIDATION_ERROR = 'static_schema_interface_validation_error',
  MISSING_NAME_TEMPLATE = 'missing_name_template',
}

export const FIELD_LEVEL_ERRORS: Array<SchemaInterfaceConstraintErrorType> = [
  SchemaInterfaceConstraintErrorType.MISSING_FIELD,
  SchemaInterfaceConstraintErrorType.INVALID_FIELD,
  SchemaInterfaceConstraintErrorType.INVALID_ARCHIVE,
  SchemaInterfaceConstraintErrorType.INVALID_FORMULA_FIELDS,
  SchemaInterfaceConstraintErrorType.OVERLAPPING_FIELDS,
  SchemaInterfaceConstraintErrorType.STATIC_SCHEMA_INTERFACE_VALIDATION_ERROR,
];

export const SCHEMA_OR_INTERFACE_LEVEL_ERRORS: Array<SchemaInterfaceConstraintErrorType> = [
  SchemaInterfaceConstraintErrorType.INVALID_BASE_BIOENTITY_TYPE,
];

interface SchemaData {
  id: string;
  name: string;
  registryId: string;
  ownerUrlId: string;
}

interface InterfaceData {
  id: string;
  name: string;
}

interface FieldNames {
  displayName: string;
  systemName: string;
}

export interface MissingFieldsData {
  errorType: SchemaInterfaceConstraintErrorType.MISSING_FIELD;
  schema: SchemaData;
  interface: InterfaceData;
  field: FieldNames;
}

export interface InvalidFieldsData {
  errorType: SchemaInterfaceConstraintErrorType.INVALID_FIELD;
  fieldName: string;
  invalidProperty: string;
  schema: SchemaData;
  interface: InterfaceData;
}

export interface InvalidBaseBioentityTypeData {
  errorType: SchemaInterfaceConstraintErrorType.INVALID_BASE_BIOENTITY_TYPE;
  interface: InterfaceData;
  schema: SchemaData;
  interfaceItemType: string;
  schemaItemType: string;
}

export interface OverlappingFieldsData {
  errorType: SchemaInterfaceConstraintErrorType.OVERLAPPING_FIELDS;
  fieldName: string;
  schema: SchemaData;
  interfaces: Array<InterfaceData>;
}

export interface InvalidArchiveData {
  errorType: SchemaInterfaceConstraintErrorType.INVALID_ARCHIVE;
  fieldName: string;
  schema: SchemaData;
  interface: InterfaceData;
}

interface InvalidFormulaFieldData {
  errorType: SchemaInterfaceConstraintErrorType.INVALID_FORMULA_FIELDS;
  fieldName: string;
  schema: SchemaData;
  interface: InterfaceData;
}

export interface MissingNameTemplateData {
  errorType: SchemaInterfaceConstraintErrorType.MISSING_NAME_TEMPLATE;
  schema: SchemaData;
  interface: InterfaceData;
}

export interface StaticSchemaInterfaceValidationErrorData {
  errorType: SchemaInterfaceConstraintErrorType.STATIC_SCHEMA_INTERFACE_VALIDATION_ERROR;
  schema: SchemaData;
  interface: InterfaceData;
  error: string;
}

type InterfaceConstraintErrorData =
  | MissingFieldsData
  | InvalidFieldsData
  | InvalidBaseBioentityTypeData
  | OverlappingFieldsData
  | InvalidArchiveData
  | InvalidFormulaFieldData
  | MissingNameTemplateData
  | StaticSchemaInterfaceValidationErrorData;

export interface InterfaceConstraintErrorDisplayDataForSchema {
  id?: string;
  error: string;
  errorType: SchemaInterfaceConstraintErrorType;
}

export interface InterfaceConstraintErrorDisplayDataForInterface {
  id: string;
  error: string;
  registryId: string;
  ownerUrlId: string;
  errorType: SchemaInterfaceConstraintErrorType;
}

interface GenericResponseJSON {
  interfaceConstraintError?: never;
  userMessage?: Optional<string>;
  devMessage?: Optional<string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

interface InterfaceConstraintErrorResponseJSON {
  interfaceConstraintError: true;
  userMessage: string;
  devMessage?: Optional<string>;
  errors: Array<InterfaceConstraintErrorData>;
}

export type ResponseJSON = GenericResponseJSON | InterfaceConstraintErrorResponseJSON;

export function compileMissingFieldsErrorsForSchema(
  invalidData: MissingFieldsData
): InterfaceConstraintErrorDisplayDataForSchema {
  return {
    id: invalidData.interface.id,
    error: `This schema has no field that matches "${invalidData.field.displayName}" (${invalidData.field.systemName}) of ${schemaFieldsetName()} "${invalidData.interface.name}"`,
    errorType: SchemaInterfaceConstraintErrorType.MISSING_FIELD,
  };
}

export function compileMissingFieldsErrorsForInterface(
  invalidData: MissingFieldsData
): InterfaceConstraintErrorDisplayDataForInterface {
  return {
    id: invalidData.schema.id,
    registryId: invalidData.schema.registryId,
    ownerUrlId: invalidData.schema.ownerUrlId,
    error: `Schema "${invalidData.schema.name}" has no field that matches "${invalidData.field.displayName}" (${invalidData.field.systemName})`,
    errorType: SchemaInterfaceConstraintErrorType.MISSING_FIELD,
  };
}

export function compileInvalidFieldsErrorsForSchema(
  invalidData: InvalidFieldsData
): InterfaceConstraintErrorDisplayDataForSchema {
  return {
    id: invalidData.interface.id,
    error: `"${invalidData.fieldName}" > "${invalidData.invalidProperty}" property does not match values with "${invalidData.fieldName}" on ${schemaFieldsetName()} "${invalidData.interface.name}."`,
    errorType: SchemaInterfaceConstraintErrorType.INVALID_FIELD,
  };
}

export function compileInvalidFieldsErrorsForInterface(
  invalidData: InvalidFieldsData
): InterfaceConstraintErrorDisplayDataForInterface {
  return {
    id: invalidData.schema.id,
    registryId: invalidData.schema.registryId,
    ownerUrlId: invalidData.schema.ownerUrlId,
    error: `Schema "${invalidData.schema.name}" has conflicting value for property "${invalidData.fieldName}" > "${invalidData.invalidProperty}"`,
    errorType: SchemaInterfaceConstraintErrorType.INVALID_FIELD,
  };
}

// REST error responses serialize item types as lowercase (e.g. "basic_folder_item"),
// but the frontend TagSchemaItemType enum uses UPPER_CASE. Both derive from the same
// Python enum (TagSchema.ItemType), just serialized differently: GraphQL uses member
// names (UPPER_CASE) while REST uses str(value) (lowercase). This helper normalizes
// and converts to a display name, with a fallback to the raw string for unknown types.
function getItemTypeDisplayNameFromString(rawItemType: string): string {
  const normalized = rawItemType.toUpperCase();
  const match = Object.values(TagSchemaItemType).find((v) => v === normalized);
  return match != null ? getItemTypeDisplayName(match) : rawItemType;
}

export function compileInvalidBaseBioentityTypeErrorsForSchema(
  invalidData: InvalidBaseBioentityTypeData
): InterfaceConstraintErrorDisplayDataForSchema {
  return {
    id: invalidData.interface.id,
    error: `The entity type of this schema does not match the entity type of ${schemaFieldsetName()} "${invalidData.interface.name}": "${getItemTypeDisplayNameFromString(invalidData.interfaceItemType)}"`,
    errorType: SchemaInterfaceConstraintErrorType.INVALID_BASE_BIOENTITY_TYPE,
  };
}

export function compileInvalidBaseBioentityTypeErrorsForInterface(
  invalidData: InvalidBaseBioentityTypeData
): InterfaceConstraintErrorDisplayDataForInterface {
  return {
    id: invalidData.schema.id,
    registryId: invalidData.schema.registryId,
    ownerUrlId: invalidData.schema.ownerUrlId,
    error: `Schema "${invalidData.schema.name}" has conflicting entity type "${getItemTypeDisplayNameFromString(invalidData.schemaItemType)}"`,
    errorType: SchemaInterfaceConstraintErrorType.INVALID_BASE_BIOENTITY_TYPE,
  };
}

export function compileOverlappingFieldsErrorsForSchema(
  invalidData: OverlappingFieldsData
): InterfaceConstraintErrorDisplayDataForSchema {
  const interfaceNames = invalidData.interfaces.map((i) => i.name);
  return {
    error: `Multiple ${schemaFieldsetNamePluralized()} have field "${invalidData.fieldName}": "${interfaceNames.join('", "')}"`,
    errorType: SchemaInterfaceConstraintErrorType.OVERLAPPING_FIELDS,
  };
}

export function compileOverlappingFieldsErrorsForInterface(
  invalidData: OverlappingFieldsData
): InterfaceConstraintErrorDisplayDataForInterface {
  const interfaceNames = invalidData.interfaces.map((i) => i.name);
  return {
    id: invalidData.schema.id,
    registryId: invalidData.schema.registryId,
    ownerUrlId: invalidData.schema.ownerUrlId,
    error: `Schema "${invalidData.schema.name}" has field "${
      invalidData.fieldName
    }" that exists on multiple ${schemaFieldsetNamePluralized()}: "${interfaceNames.join(', ')}"`,
    errorType: SchemaInterfaceConstraintErrorType.OVERLAPPING_FIELDS,
  };
}

export function compileInvalidArchiveErrorsForSchema(
  invalidData: InvalidArchiveData
): InterfaceConstraintErrorDisplayDataForSchema {
  return {
    id: invalidData.interface.id,
    error: `Cannot archive field "${invalidData.fieldName}" because it is found on ${schemaFieldsetName()} "${invalidData.interface.name}"`,
    errorType: SchemaInterfaceConstraintErrorType.INVALID_ARCHIVE,
  };
}

export function compileInvalidArchiveErrorsForInterface(
  invalidData: InvalidArchiveData
): InterfaceConstraintErrorDisplayDataForInterface {
  return {
    id: invalidData.schema.id,
    registryId: invalidData.schema.registryId,
    ownerUrlId: invalidData.schema.ownerUrlId,
    error: `Field "${invalidData.fieldName}" cannot be applied on schema "${invalidData.schema.name}" because the schema's matching field is archived.`,
    errorType: SchemaInterfaceConstraintErrorType.INVALID_ARCHIVE,
  };
}

function compileInvalidFormulaFieldErrorsForSchema(
  invalidData: InvalidFormulaFieldData
): InterfaceConstraintErrorDisplayDataForSchema {
  return {
    id: invalidData.interface.id,
    error: `Field "${invalidData.fieldName}" cannot be constrained by ${schemaFieldsetName()} "${invalidData.interface.name}" because it is a snapshot or computed field.`,
    errorType: SchemaInterfaceConstraintErrorType.INVALID_FORMULA_FIELDS,
  };
}

function compileInvalidFormulaFieldErrorsForInterface(
  invalidData: InvalidFormulaFieldData
): InterfaceConstraintErrorDisplayDataForInterface {
  return {
    id: invalidData.schema.id,
    registryId: invalidData.schema.registryId,
    ownerUrlId: invalidData.schema.ownerUrlId,
    error: `Schema "${invalidData.schema.name}" cannot have a snapshot or computed field that matches "${invalidData.fieldName}"`,
    errorType: SchemaInterfaceConstraintErrorType.INVALID_FORMULA_FIELDS,
  };
}

export function compileMissingNameTemplateErrorsForSchema(
  invalidData: MissingNameTemplateData
): InterfaceConstraintErrorDisplayDataForSchema {
  return {
    id: invalidData.schema.id,
    error: `The name template on schema "${invalidData.schema.name}" needs to match the name template on ${schemaFieldsetName()} "${invalidData.interface.name}"`,
    errorType: SchemaInterfaceConstraintErrorType.MISSING_NAME_TEMPLATE,
  };
}

export function compileMissingNameTemplateErrorsForInterface(
  invalidData: MissingNameTemplateData
): InterfaceConstraintErrorDisplayDataForInterface {
  return {
    id: invalidData.schema.id,
    registryId: invalidData.schema.registryId,
    ownerUrlId: invalidData.schema.ownerUrlId,
    error: `The name template on schema "${invalidData.schema.name}" needs to match the name template on ${schemaFieldsetName()} "${invalidData.interface.name}"`,
    errorType: SchemaInterfaceConstraintErrorType.MISSING_NAME_TEMPLATE,
  };
}

function compileStaticSchemaInterfaceValidationErrorForSchema(
  invalidData: StaticSchemaInterfaceValidationErrorData
): InterfaceConstraintErrorDisplayDataForSchema {
  // If this assertion fails, then that means we can link the user to the Static Interface. So, we should (1)
  // include `id: invalidData.interface.id` in the return value and (2) remove the assertion.
  assertAndContinue(
    getSchemaInterfaceURL(invalidData.interface.id) == null,
    'Expected Static Schema Interface to not have a view URL'
  );
  return {
    // As mentioned above, we omit `id: invalidData.interface.id` because we cannot link the user to the
    // Static Interface.
    error: invalidData.error,
    errorType: SchemaInterfaceConstraintErrorType.STATIC_SCHEMA_INTERFACE_VALIDATION_ERROR,
  };
}

function compileStaticSchemaInterfaceValidationErrorForInterface(
  invalidData: StaticSchemaInterfaceValidationErrorData
): InterfaceConstraintErrorDisplayDataForInterface {
  return {
    id: invalidData.schema.id,
    registryId: invalidData.schema.registryId,
    ownerUrlId: invalidData.schema.ownerUrlId,
    error: invalidData.error,
    errorType: SchemaInterfaceConstraintErrorType.STATIC_SCHEMA_INTERFACE_VALIDATION_ERROR,
  };
}

/**
 * Find the first unknown error type in the response JSON. If no unknown error type is found, return `null`.
 *
 * An error type is considered unknown if it is not one of the known `SchemaInterfaceConstraintErrorType`.
 * This happens when the server sends an error type that is not recognized by the client.
 */
function findUnknownErrorType(responseJSON: InterfaceConstraintErrorResponseJSON): string | null {
  const errorTypes = responseJSON.errors.map((e) => e.errorType);
  return errorTypes.find((t) => !Object.values(SchemaInterfaceConstraintErrorType).includes(t)) ?? null;
}

export function getInterfaceConstraintErrorsForSchema(
  responseJSON: ResponseJSON
): Array<InterfaceConstraintErrorDisplayDataForSchema> | null {
  if (responseJSON && responseJSON.interfaceConstraintError) {
    const firstUnknownErrorType = findUnknownErrorType(responseJSON);
    if (firstUnknownErrorType != null) {
      logging.error(`Unknown ${schemaFieldsetName()} constraint error received: ${firstUnknownErrorType}`);
      // Return null to indicate that we cannot parse the error. This lets the caller do its default error
      // handling.
      return null;
    }

    // oxlint-disable-next-line array-callback-return
    return responseJSON.errors.map((error) => {
      switch (error.errorType) {
        case SchemaInterfaceConstraintErrorType.MISSING_FIELD:
          return compileMissingFieldsErrorsForSchema(error);
        case SchemaInterfaceConstraintErrorType.INVALID_FIELD:
          return compileInvalidFieldsErrorsForSchema(error);
        case SchemaInterfaceConstraintErrorType.INVALID_BASE_BIOENTITY_TYPE:
          return compileInvalidBaseBioentityTypeErrorsForSchema(error);
        case SchemaInterfaceConstraintErrorType.OVERLAPPING_FIELDS:
          return compileOverlappingFieldsErrorsForSchema(error);
        case SchemaInterfaceConstraintErrorType.INVALID_ARCHIVE:
          return compileInvalidArchiveErrorsForSchema(error);
        case SchemaInterfaceConstraintErrorType.INVALID_FORMULA_FIELDS:
          return compileInvalidFormulaFieldErrorsForSchema(error);
        case SchemaInterfaceConstraintErrorType.MISSING_NAME_TEMPLATE:
          return compileMissingNameTemplateErrorsForSchema(error);
        case SchemaInterfaceConstraintErrorType.STATIC_SCHEMA_INTERFACE_VALIDATION_ERROR:
          return compileStaticSchemaInterfaceValidationErrorForSchema(error);
        default:
          throw new Error(
            `Unexpectedly encountered ${schemaFieldsetName()} constraint error ${assertNever(
              error
            )}. It should have been caught by \`findUnknownErrorTypes\` earlier.`
          );
      }
    });
  }
  return null;
}

export function getInterfaceConstraintErrorsForInterface(
  responseJSON: ResponseJSON
): Array<InterfaceConstraintErrorDisplayDataForInterface> | null {
  if (responseJSON && responseJSON.interfaceConstraintError) {
    const firstUnknownErrorType = findUnknownErrorType(responseJSON);
    if (firstUnknownErrorType != null) {
      logging.error(`Unknown ${schemaFieldsetName()} constraint error received: ${firstUnknownErrorType}`);
      // Return null to indicate that we cannot parse the error. This lets the caller do its default error
      // handling.
      return null;
    }

    // oxlint-disable-next-line array-callback-return
    return responseJSON.errors.map((error) => {
      switch (error.errorType) {
        case SchemaInterfaceConstraintErrorType.MISSING_FIELD:
          return compileMissingFieldsErrorsForInterface(error);
        case SchemaInterfaceConstraintErrorType.INVALID_FIELD:
          return compileInvalidFieldsErrorsForInterface(error);
        case SchemaInterfaceConstraintErrorType.INVALID_BASE_BIOENTITY_TYPE:
          return compileInvalidBaseBioentityTypeErrorsForInterface(error);
        case SchemaInterfaceConstraintErrorType.OVERLAPPING_FIELDS:
          return compileOverlappingFieldsErrorsForInterface(error);
        case SchemaInterfaceConstraintErrorType.INVALID_ARCHIVE:
          return compileInvalidArchiveErrorsForInterface(error);
        case SchemaInterfaceConstraintErrorType.INVALID_FORMULA_FIELDS:
          return compileInvalidFormulaFieldErrorsForInterface(error);
        case SchemaInterfaceConstraintErrorType.STATIC_SCHEMA_INTERFACE_VALIDATION_ERROR:
          // This case is not possible because `getInterfaceConstraintErrorsForInterface` is only called for
          // Custom Interfaces, as of 2024-04-28. However we implement it for uniformity, so it is easier to
          // reason about.
          return compileStaticSchemaInterfaceValidationErrorForInterface(error);
        case SchemaInterfaceConstraintErrorType.MISSING_NAME_TEMPLATE:
          return compileMissingNameTemplateErrorsForInterface(error);
        default:
          throw new Error(
            `Unexpectedly encountered ${schemaFieldsetName()} constraint error ${assertNever(
              error
            )}. It should have been caught by \`findUnknownErrorTypes\` earlier.`
          );
      }
    });
  }
  return null;
}
